import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "../styles/OurServices.css";
import ClientLogos from "./ClientLogos";
import CompanyProfile from "./CompanyProfile";

import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import {
  FaUtensils,
  FaWallet,
  FaRegBuilding,
  FaMapMarkedAlt,
  FaCalendarAlt,
  FaConciergeBell,
} from "react-icons/fa";

import {
  MdOutlineHealthAndSafety,
  MdOutlineFactory,
  MdOutlineEmojiFoodBeverage,
} from "react-icons/md";

const StatCounter = ({ icon, end, suffix, label }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const IconComponent = icon;

  return (
    <div className="app-expertise__stat-item" ref={ref}>
      <IconComponent className="app-expertise__stat-icon" />
      <h3 className="app-expertise__stat-number">
        {inView ? <CountUp start={0} end={end} duration={2.5} /> : "0"}
        {suffix}
      </h3>
      <p className="app-expertise__stat-label">{label}</p>
    </div>
  );
};


function OurServices() {
  return (
    <>
     <Helmet>
        <title>Our Services | Industrial Catering Services</title>
        <meta
          name="description"
          content="Expert industrial catering since 2009. Custom canteen solutions for manufacturers across 5 states. Serving 17,500+ meals daily."
        />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        <link
          rel="canonical"
          href="https://laxmihospitalityservices.in/services"
        />

        {/* Open Graph Meta Tags */}
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://laxmihospitalityservices.in/services"
        />
        <meta
          property="og:title"
          content="Industrial Catering Services | Laxmi Hospitality Services"
        />
        <meta
          property="og:description"
          content="Expert industrial catering since 2009. Custom canteen solutions for manufacturers across 5 states. Serving 14,550+ meals daily."
        />
        <meta property="og:site_name" content="Laxmi Hospitality Services" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:image" content="https://laxmihospitalityservices.in/images/Our-Services-Banner.png" />

        {/* Industrial Catering Service JSON-LD Schema */}
        <script type="application/ld+json">
          {`
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Industrial Catering Services",
        "description": "Comprehensive industrial catering solutions including canteen management, food service, and employee meal programs for manufacturing facilities, factories, and industrial plants across India.",
        "provider": {
          "@type": "Organization",
          "@id": "https://laxmihospitalityservices.in/#organization",
          "name": "Laxmi Hospitality Services",
          "url": "https://laxmihospitalityservices.in"
        },
        "serviceType": "Industrial Food Service",
        "category": "Industrial Catering",
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "availableChannel": {
          "@type": "ServiceChannel",
          "url": "https://laxmihospitalityservices.in/services",
          "name": "Industrial Catering Services"
        },
        "audience": {
          "@type": "Audience",
          "audienceType": "Manufacturing Companies",
          "geographicArea": "India"
        },
        "serviceOutput": "Daily meal service for industrial workforce",
        "url": "https://laxmihospitalityservices.in/services",
        "sameAs": [
          "https://www.linkedin.com/company/laxmi-hospitality-services/",
          "https://www.instagram.com/laxmi_hospitality_services/"
        ]
      }
    `}
  </script>

  {/* -------- OUr TEAM PAGE BREADCRUMB -------- */}
  <script type="application/ld+json">
    {`
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": "https://laxmihospitalityservices.in/#breadcrumb",
        "name": "Website Breadcrumb",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://laxmihospitalityservices.in/" },
          { "@type": "ListItem", "position": 2, "name": "Our Services", "item": "https://laxmihospitalityservices.in/services" }
        ]
      }
    `}
        </script>
      </Helmet>

    
      <div className="app-our-services__container">
        {/* Banner Section */}
        <div className="app-services-banner__wrapper">
          <img
            src={"/images/Our-Services-Banner.png"}
            alt="Our Services banner"
            className="app-services-banner__image"
          />
          <div className="app-services-banner__overlay">
            <h1 className="app-services-banner__title">Our Services</h1>
            <p className="app-services-banner__breadcrumb">
              <Link to="/" className="app-services-banner__breadcrumb-link">
                Home
              </Link>{" "}
              / Services
            </p>
          </div>
        </div>

        
        {/* NEW EXPERTISE SECTION */}
        <section className="app-expertise__section">
          <div className="app-expertise__content">
            <h2 className="app-expertise__title">
              Powering India's <span className="app-expertise__title--highlight">Industrial Catering</span> since 2009
            </h2>
            <p className="app-expertise__description">
              Since <strong>2009</strong>, Laxmi Hospitality Services has been a trusted leader in specialized industrial catering, building our reputation on custom-tailored solutions. Our footprint spans <strong>5 states and UTs</strong>, serving over <strong>16 key cities</strong> from Mumbai to Anantapur. At <strong>25+ client locations</strong>, we proudly deliver <strong>17,500+ fresh meals daily</strong>, powering India's leading manufacturing units and energy sites.
            </p>
            <div className="app-expertise__stats-container">
              <StatCounter
                icon={FaUtensils}
                end={17500}
                suffix="+"
                label="Meals Served Daily"
              />
              <StatCounter
                icon={FaRegBuilding}
                end={25}
                suffix="+"
                label="Client Locations"
              />
              <StatCounter
                icon={FaMapMarkedAlt}
                end={16}
                suffix="+"
                label="Cities Served"
              />
              <StatCounter
                icon={FaCalendarAlt}
                end={2009}
                suffix=""
                label="Serving Since"
              />
            </div>
          </div>
        </section>
        {/* --- END: NEW EXPERTISE SECTION --- */}

        <CompanyProfile />

        {/* What We Offer Section */}
        <section className="app-services__content-section">
          <p className="app-services__greeting">WHAT WE OFFER</p>
          <h2 className="app-services__main-heading">
            Smart, Scalable, and Soulful Canteen Services That Power Industries
          </h2>
          <div className="app-services__description">
            <p>
              <strong>At Laxmi Hospitality Service</strong>, we don’t just serve
              food—we build canteen ecosystems designed for efficiency,
              nourishment, and long-term satisfaction. Whether you’re running a
              factory floor, a textile mill, or an engineering hub, our
              solutions are built for your people and your pace.
            </p>
          </div>
          <div className="app-services__list-grid">
            <div className="app-services__list-item">
              <FaConciergeBell className="app-services__list-icon" />
              <h3>End-to-End Industrial Canteen Solutions</h3>
              <p>
                From infrastructure setup to daily meal service, we handle every
                aspect of your canteen with precision and professionalism. Our
                trained teams ensure seamless operations that run like
                clockwork.
              </p>
            </div>
            <div className="app-services__list-item">
              <FaUtensils className="app-services__list-icon" />
              <h3>Fully Customizable Menus</h3>
              <p>
                We craft menus tailored to your workforce’s dietary preferences,
                shift schedules, and regional tastes—always wholesome, always
                satisfying. Vegetarian, calorie-conscious, or culturally
                specific? We’ve got you covered.
              </p>
            </div>
            <div className="app-services__list-item">
              <FaWallet className="app-services__list-icon" />
              <h3>Budget-Conscious, Value-Driven</h3>
              <p>
                Get the most out of your catering investment. Our pricing models
                are transparent, scalable, and optimized to deliver maximum
                value—whether you're feeding 30 or 1,000+ employees.
              </p>
            </div>
            <div className="app-services__list-item">
              <MdOutlineHealthAndSafety className="app-services__list-icon" />
              <h3>Fresh, Hygienic, and Energizing Food</h3>
              <p>
                Every meal is prepared with fresh ingredients under strict
                hygiene protocols. Our goal is simple: to fuel your workforce
                with energizing food that supports productivity without
                compromise.
              </p>
            </div>
            <div className="app-services__list-item">
              <MdOutlineFactory className="app-services__list-icon" />
              <h3>Industrial Expertise with Human Insight</h3>
              <p>
                We understand shift rotations, staggered break timings, and the
                operational intensity of industrial life. Our services are
                designed to keep pace with your workforce—and never miss a beat.
              </p>
            </div>
            <div className="app-services__list-item">
              <MdOutlineEmojiFoodBeverage className="app-services__list-icon" />
              <h3>Employee Wellness & Nutrition Programs</h3>
              <p>
                Beyond meals, we offer wellness-driven options curated by
                nutritionists— helping your workforce stay energized, healthy,
                and focused. Boost productivity with meals designed for physical
                and mental well-being.
              </p>
            </div>
          </div>
        </section>


        {/* Why Choose Us Section */}
        <section className="why-laxmi-section">
          <div className="container">
            <h2 className="why-laxmi-title">
              Why Choose Laxmi Hospitality Service?
            </h2>
            <p className="why-laxmi-subtitle">
              We understand that keeping your workforce well-fed, healthy, and
              satisfied isn't just about food—it's about operational excellence,
              consistency, and care.
            </p>

            <div className="why-laxmi-grid">
              <div className="why-laxmi-card">
                <h3>Seamless Operations, Zero Hassles</h3>
                <p>
                  You have enough on your plate. Let us take care of what goes
                  on your team’s. From setup to daily service, our canteen
                  solutions are designed for efficiency and peace of mind.
                </p>
              </div>

              <div className="why-laxmi-card">
                <h3>Scalable Services for Every Team Size</h3>
                <p>
                  Whether you're a facility of 30 or a workforce of 1,000+, our
                  services scale to match your needs without compromising on
                  quality or delivery.
                </p>
              </div>

              <div className="why-laxmi-card">
                <h3>Menus Everyone Will Love</h3>
                <p>
                  We plan diverse, nourishing menus tailored to your employees’
                  tastes and dietary needs—from regional favorites to special
                  requests. Happy employees start with happy meals.
                </p>
              </div>

              <div className="why-laxmi-card">
                <h3>Industrial Expertise with a Human Touch</h3>
                <p>
                  We specialize in industrial canteen services, so we understand
                  the demands of factory shifts, break schedules, and
                  nutritional balance like no one else. Reliability and
                  punctuality are our hallmarks.
                </p>
              </div>

              <div className="why-laxmi-card">
                <h3>Adaptable & Reliable—Even on Short Notice</h3>
                <p>
                  Unexpected demand? Schedule change? We’re equipped to handle
                  dynamic environments with calm professionalism and prompt
                  service.
                </p>
              </div>

              <div className="why-laxmi-card">
                <h3>Transparent Pricing, Exceptional Value</h3>
                <p>
                  Our services are designed to be cost-effective, without
                  cutting corners. We offer competitive pricing that aligns with
                  your budget and expectations.
                </p>
              </div>

              <div className="why-laxmi-card">
                <h3>Committed to Long-Term Partnerships</h3>
                <p>
                  We don’t just serve meals—we build relationships. Many of our
                  clients have stayed with us for years because we deliver
                  consistency, care, and results they can count on.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
      <ClientLogos/>
    </>
  );
}

export default OurServices;
